import React, { useRef } from 'react'
import { IconButton } from '../../../ui'

const SESSION_COLOR = '#9b6cc4'

// toggleMode: 'discussed' | 'status'
// 'discussed': circle toggle, discussed items get filled circle + strikethrough
// 'status': badge cycles open ↔ answered

export function ToggleList({ items = [], onChange, maxItems = 15, maxChars = 140, readOnly = false, toggleMode = 'discussed', onPullFromPrep }) {
  const inputRefs = useRef([])

  function update(index, patch) {
    const next = items.map((item, i) => (i === index ? { ...item, ...patch } : item))
    onChange({ items: next })
  }

  function handleAdd() {
    if (items.length >= maxItems) return
    const newItem = toggleMode === 'status'
      ? { text: '', status: 'open' }
      : { text: '', discussed: false }
    onChange({ items: [...items, newItem] })
    setTimeout(() => inputRefs.current[items.length]?.focus(), 0)
  }

  function handleRemove(index) {
    onChange({ items: items.filter((_, i) => i !== index) })
  }

  function handleToggle(index) {
    if (readOnly) return
    if (toggleMode === 'status') {
      const current = items[index].status
      update(index, { status: current === 'open' ? 'answered' : 'open' })
    } else {
      update(index, { discussed: !items[index].discussed })
    }
  }

  function handleKeyDown(e, index) {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    } else if (e.key === 'Backspace' && items[index]?.text === '' && items.length > 0) {
      e.preventDefault()
      handleRemove(index)
      setTimeout(() => inputRefs.current[Math.max(0, index - 1)]?.focus(), 0)
    }
  }

  function renderToggle(item, index) {
    if (toggleMode === 'status') {
      const isAnswered = item.status === 'answered'
      return (
        <button
          style={{
            ...styles.statusBadge,
            ...(isAnswered ? styles.statusAnswered : styles.statusOpen),
          }}
          onClick={() => handleToggle(index)}
          disabled={readOnly}
          aria-label={`Status: ${item.status}`}
        >
          {isAnswered ? 'answered' : 'open'}
        </button>
      )
    }
    const discussed = item.discussed
    return (
      <button
        style={{ ...styles.circle, ...(discussed ? styles.circleFilled : {}) }}
        onClick={() => handleToggle(index)}
        disabled={readOnly}
        aria-label={discussed ? 'Mark not discussed' : 'Mark discussed'}
      />
    )
  }

  return (
    <div style={styles.wrap}>
      {items.map((item, index) => {
        const discussed = toggleMode === 'discussed' && item.discussed
        return (
          <div key={index} style={styles.row}>
            {renderToggle(item, index)}
            <input
              ref={(el) => { inputRefs.current[index] = el }}
              style={{
                ...styles.input,
                textDecoration: discussed ? 'line-through' : 'none',
                opacity: discussed ? 0.5 : 1,
              }}
              value={item.text}
              maxLength={maxChars}
              disabled={readOnly}
              placeholder={readOnly ? '' : toggleMode === 'status' ? 'Question…' : 'Talking point…'}
              onChange={(e) => update(index, { text: e.target.value })}
              onKeyDown={(e) => handleKeyDown(e, index)}
              aria-label={`Item ${index + 1}`}
            />
            {!readOnly && (
              <IconButton ariaLabel="Remove item" onClick={() => handleRemove(index)} tabIndex={-1}>×</IconButton>
            )}
          </div>
        )
      })}
      {!readOnly && (
        <div style={styles.footer}>
          {items.length < maxItems && (
            <button style={styles.addBtn} onClick={handleAdd}>+ add</button>
          )}
          {onPullFromPrep && (
            <>
              {items.length < maxItems && <span style={styles.divider}>|</span>}
              <button style={styles.pullBtn} onClick={onPullFromPrep}>pull from prep</button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

const styles = {
  wrap: { padding: '10px 12px 12px', display: 'flex', flexDirection: 'column', gap: '5px' },
  row: { display: 'flex', alignItems: 'center', gap: '7px' },
  circle: {
    width: '12px', height: '12px', borderRadius: '50%', flexShrink: 0,
    border: `1.5px solid ${SESSION_COLOR}`, background: 'transparent',
    cursor: 'pointer', padding: 0,
  },
  circleFilled: { background: SESSION_COLOR },
  statusBadge: {
    fontSize: '8px', fontWeight: 700, padding: '2px 5px', borderRadius: '4px',
    border: 'none', cursor: 'pointer', flexShrink: 0, lineHeight: 1.4, whiteSpace: 'nowrap',
  },
  statusOpen: { background: '#e89a5d18', color: '#8a5a2a', border: '1px solid #e89a5d44' },
  statusAnswered: { background: '#3a8a5a18', color: '#2d5a3a', border: '1px solid #3a8a5a44' },
  input: {
    flex: 1, border: 'none', outline: 'none', background: 'transparent',
    fontSize: '12px', color: 'var(--text-primary)', lineHeight: '1.5', padding: '2px 0',
  },
  footer: { display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' },
  addBtn: {
    background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px',
    color: SESSION_COLOR, padding: '2px 0', textAlign: 'left',
  },
  divider: { fontSize: '11px', color: 'var(--text-secondary)', opacity: 0.3 },
  pullBtn: {
    background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px',
    color: 'var(--text-secondary)', padding: '2px 0', textAlign: 'left',
  },
}
